import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useApp } from '@/context/AppContext';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { requestLimit } from '@/lib/limit-check';
import { useBranches } from '@/hooks/api/branches';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  ArrowLeft, Phone, CreditCard, FileText, MessageSquare,
  ShoppingBag, Calendar, User, Shield, Send, Loader2, RefreshCw, CheckCircle, Clock, AlertTriangle, ShoppingCart,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

type CustomerStatus = 'lead' | 'active' | 'blocked';
type CustomerSource = 'pos_sale' | 'murabaha_application' | 'manual' | 'b2b';

interface Customer {
  id: number;
  full_name: string;
  phone: string;
  passport: string | null;
  pinfl: string | null;
  status: CustomerStatus;
  source: CustomerSource;
  total_purchases: number;
  total_spent: string;
  murabaha_limit: string | null;
  limit_expires_at: string | null;
  limit_by_tenor?: { tenor_months: number; limit: number }[];
  limit_check_id?: number | null;
  limit_based_on_id?: number | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  branch_id: number | null;
}

const STATUS_CONFIG: Record<CustomerStatus, { labelKey: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; color: string }> = {
  lead: { labelKey: 'cust_prof.status_lead', variant: 'outline', color: 'text-muted-foreground' },
  active: { labelKey: 'cust_prof.status_active', variant: 'default', color: 'text-primary' },
  blocked: { labelKey: 'cust_prof.status_blocked', variant: 'destructive', color: 'text-destructive' },
};

const SOURCE_LABELS: Record<CustomerSource, string> = {
  pos_sale: 'cust_common.source_pos_sale',
  murabaha_application: 'cust_common.source_murabaha',
  manual: 'cust_common.source_manual',
  b2b: 'cust_common.source_b2b',
};

function dateLocale() {
  return ({ uz: 'uz-UZ', ru: 'ru-RU', en: 'en-US' } as Record<string, string>)[i18n.language] ?? 'uz-UZ';
}

function formatUZS(amount: number) {
  return new Intl.NumberFormat(dateLocale()).format(amount) + ' ' + i18n.t('cust_common.currency');
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(dateLocale(), {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function CustomerProfile() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newNote, setNewNote] = useState('');

  const { data: branches = [] } = useBranches();

  const { data: customer, isLoading } = useQuery({
    queryKey: ['/customers/{customer}', id],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/customers/{customer}', {
        params: { path: { customer: Number(id) } },
      });
      if (error) throw error;
      return data.data as Customer;
    },
    enabled: !!id,
  });

  const updateNotesMutation = useMutation({
    mutationFn: async (notes: string) => {
      const { data, error } = await apiClient.POST('/customers/{customer}/notes', {
        params: { path: { customer: Number(id) } },
        body: { note: notes },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/customers/{customer}', id] });
      setNewNote('');
      toast({ title: t('cust_prof.note_added') });
    },
    onError: (err: any) => {
      toast({ title: t('cust_common.error'), description: err.message, variant: 'destructive' });
    },
  });

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <p className="text-muted-foreground">{t('cust_common.loading')}</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-6 space-y-4">
        <Link to="/customers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={16} /> {t('cust_prof.back')}
        </Link>
        <p className="text-muted-foreground">{t('cust_prof.not_found')}</p>
      </div>
    );
  }

  const sc = STATUS_CONFIG[customer.status];

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      {/* Back link */}
      <Link to="/customers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={16} /> {t('cust_prof.back')}
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
            <User size={28} className="text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{customer.full_name}</h1>
            <div className="flex items-center gap-3 mt-1">
              <Badge variant={sc.variant}>{t(sc.labelKey)}</Badge>
              <span className="text-sm text-muted-foreground">{t(SOURCE_LABELS[customer.source])}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <InfoCard icon={Phone} label={t('cust_common.phone')} value={customer.phone} />
        <InfoCard icon={Shield} label={t('cust_common.passport')} value={customer.passport || '—'} />
        <InfoCard icon={ShoppingBag} label={t('cust_prof.purchases_count')} value={String(customer.total_purchases)} />
        <InfoCard icon={CreditCard} label={t('cust_prof.total_spent')} value={formatUZS(Number(customer.total_spent))} />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">{t('cust_prof.tab_overview')}</TabsTrigger>
          <TabsTrigger value="purchases">{t('cust_prof.tab_purchases')}</TabsTrigger>
          <TabsTrigger value="murabaha">{t('cust_prof.tab_murabaha')}</TabsTrigger>
          <TabsTrigger value="notes">{t('cust_prof.tab_notes')}</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-5 space-y-4">
            <h3 className="font-semibold text-foreground">{t('cust_prof.personal_info')}</h3>
            <Separator />
            <div className="grid grid-cols-2 gap-4">
              <DetailRow label={t('cust_prof.full_name')} value={customer.full_name} />
              <DetailRow label={t('cust_common.phone')} value={customer.phone} />
              <DetailRow label={t('cust_common.passport')} value={customer.passport || t('cust_prof.not_provided')} />
              <DetailRow label="PINFL" value={customer.pinfl || t('cust_prof.not_provided')} />
              <DetailRow label={t('cust_common.status')} value={t(sc.labelKey)} />
              <DetailRow label={t('cust_common.source')} value={t(SOURCE_LABELS[customer.source])} />
              <DetailRow label={t('cust_common.branch')} value={branches.find((b: any) => b.id === customer.branch_id)?.name || '—'} />
              <DetailRow label={t('cust_prof.registered')} value={formatDate(customer.created_at)} />
              <DetailRow label={t('cust_prof.last_updated')} value={formatDate(customer.updated_at)} />
            </div>
          </div>
        </TabsContent>

        {/* Purchases */}
        <TabsContent value="purchases" className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingBag size={18} className="text-primary" />
              <h3 className="font-semibold text-foreground">{t('cust_prof.tab_purchases')}</h3>
            </div>
            <Separator className="mb-4" />
            {customer.total_purchases === 0 ? (
              <div className="text-center py-10">
                <ShoppingBag size={40} className="mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-muted-foreground">{t('cust_prof.no_purchases')}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('cust_prof.no_purchases_hint')}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">{t('cust_prof.total_purchases')}</span>
                  <span className="font-semibold text-foreground">{t('cust_prof.count_unit', { n: customer.total_purchases })}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">{t('cust_list.col_total')}</span>
                  <span className="font-semibold text-foreground">{formatUZS(Number(customer.total_spent))}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">{t('cust_prof.avg_check')}</span>
                  <span className="font-semibold text-foreground">
                    {formatUZS(customer.total_purchases > 0 ? Number(customer.total_spent) / customer.total_purchases : 0)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Murabaha */}
        <TabsContent value="murabaha" className="space-y-4">
          <MurabahaTab customer={customer} customerId={customer.id} />
        </TabsContent>

        {/* Notes */}
        <TabsContent value="notes" className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare size={18} className="text-primary" />
              <h3 className="font-semibold text-foreground">{t('cust_prof.tab_notes')}</h3>
            </div>
            <Separator className="mb-4" />

            {/* Add note */}
            <div className="flex gap-2 mb-4">
              <Textarea
                placeholder={t('cust_prof.note_placeholder')}
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
            <Button
              size="sm"
              onClick={() => updateNotesMutation.mutate(newNote)}
              disabled={!newNote.trim() || updateNotesMutation.isPending}
              className="mb-4"
            >
              <Send size={14} className="mr-1" />
              {updateNotesMutation.isPending ? t('cust_common.saving') : t('cust_prof.add_note')}
            </Button>

            {/* Existing notes */}
            {customer.notes ? (
              <div className="bg-muted/30 rounded-lg p-4">
                <pre className="text-sm text-foreground whitespace-pre-wrap font-sans leading-relaxed">
                  {customer.notes}
                </pre>
              </div>
            ) : (
              <div className="text-center py-8">
                <MessageSquare size={32} className="mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">{t('cust_prof.no_notes')}</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-sm font-semibold text-foreground truncate">{value}</p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm text-foreground font-medium mt-0.5">{value}</p>
    </div>
  );
}

function isLimitExpired(c: Customer) {
  return c.limit_expires_at && new Date(c.limit_expires_at) < new Date();
}

function MurabahaTab({ customer, customerId }: { customer: Customer; customerId: number }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setMurabahaCustomer, clearCart } = useApp();
  const [limitResult, setLimitResult] = useState<{ limit: number; message: string } | null>(null);

  const checkLimitMutation = useMutation({
    mutationFn: async () => {
      // Sabatsiz (POS tashqarisida) tekshiruv — basket_amount: 0 (backend min:0 ni qabul qiladi)
      // T224: the bank scores asynchronously — requestLimit polls our status until it is final
      const data = await requestLimit(customerId, { basket_amount: 0 });

      const sixMonths = data.limits.find(l => l.tenor_months === 6);
      const threeMonths = data.limits.find(l => l.tenor_months === 3);
      const limit = sixMonths?.limit ?? threeMonths?.limit ?? data.limits[0]?.limit ?? 0;
      const message = data.status === 'rejected'
        ? (data.error_code === 'NO_INCOME_DATA' ? t('loan_limit.no_income_data') : t('loan_limit.rejected'))
        : data.approved ? t('cust_prof.limit_found') : t('cust_prof.limit_found_insufficient');
      return { limit, message };
    },
    onSuccess: (result) => {
      setLimitResult(result);
      queryClient.invalidateQueries({ queryKey: ['/customers/{customer}', String(customerId)] });
      queryClient.invalidateQueries({ queryKey: ['/customers'] });
      toast({ title: t('cust_prof.limit_checked'), description: result.message });
    },
    onError: (err: any) => {
      toast({ title: t('cust_common.error'), description: err.message || t('cust_prof.limit_check_error'), variant: 'destructive' });
    },
  });

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <FileText size={18} className="text-primary" />
        <h3 className="font-semibold text-foreground">{t('cust_prof.murabaha_status')}</h3>
      </div>
      <Separator className="mb-4" />

      <div className="space-y-4">
        {/* Current limit display */}
        {(() => {
          const isExpired = customer.limit_expires_at && new Date(customer.limit_expires_at) < new Date();
          const expiresAt = customer.limit_expires_at ? new Date(customer.limit_expires_at) : null;
          const daysLeft = expiresAt ? Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;

          return (
            <div className={`p-4 rounded-lg ${isExpired ? 'bg-destructive/5 border border-destructive/20' : 'bg-muted/30'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">{t('cust_prof.murabaha_limit_label')}</p>
                  <p className={`text-xl font-bold mt-1 ${isExpired ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                    {customer.murabaha_limit ? formatUZS(Number(customer.murabaha_limit)) : t('cust_prof.not_set')}
                  </p>
                  {customer.limit_check_id && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {customer.limit_based_on_id
                        ? t('cust_prof.limit_based_on', { id: customer.limit_check_id, base: customer.limit_based_on_id })
                        : t('cust_prof.limit_from_bank', { id: customer.limit_check_id })}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  {isExpired ? (
                    <Badge variant="destructive">
                      <AlertTriangle size={10} className="mr-1" /> {t('cust_prof.expired')}
                    </Badge>
                  ) : customer.murabaha_limit ? (
                    <Badge variant="default">{t('cust_prof.limit_exists')}</Badge>
                  ) : (
                    <Badge variant="outline">{t('cust_prof.no_limit')}</Badge>
                  )}
                </div>
              </div>
              {expiresAt && (
                <div className={`flex items-center gap-1.5 mt-2 text-xs ${isExpired ? 'text-destructive' : daysLeft && daysLeft <= 3 ? 'text-orange-600' : 'text-muted-foreground'}`}>
                  <Clock size={12} />
                  {isExpired
                    ? t('cust_prof.expired_on', { date: expiresAt.toLocaleDateString(dateLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' }) })
                    : t('cust_prof.valid_until', { date: expiresAt.toLocaleDateString(dateLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' }), days: daysLeft })
                  }
                </div>
              )}
              {/* T229: the remaining 12-month limit spread over each tenor of the merchant. */}
              {!isExpired && customer.limit_by_tenor && customer.limit_by_tenor.length > 0 && (
                <Table className="mt-3">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="h-8 px-2 text-xs">{t('cust_prof.tenor_col')}</TableHead>
                      <TableHead className="h-8 px-2 text-xs text-right">{t('cust_prof.tenor_limit_col')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customer.limit_by_tenor.map((row) => (
                      <TableRow key={row.tenor_months}>
                        <TableCell className="px-2 py-1.5">{t('cust_prof.tenor_months', { n: row.tenor_months })}</TableCell>
                        <TableCell className="px-2 py-1.5 text-right font-medium">{formatUZS(row.limit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          );
        })()}

        {/* Check limit button */}
        <div className="p-4 border border-dashed border-border rounded-lg bg-muted/20">
          <div className="flex items-start gap-3">
            <CreditCard size={20} className="text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">
                {customer.murabaha_limit ? t('cust_prof.recheck_limit') : t('cust_prof.determine_limit')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {customer.murabaha_limit
                  ? t('cust_prof.recheck_hint')
                  : t('cust_prof.determine_hint')}
              </p>
              <Button
                size="sm"
                className="mt-3"
                onClick={() => checkLimitMutation.mutate()}
                disabled={checkLimitMutation.isPending}
              >
                {checkLimitMutation.isPending ? (
                  <><Loader2 size={14} className="mr-1.5 animate-spin" /> {t('cust_prof.checking')}</>
                ) : customer.murabaha_limit ? (
                  <><RefreshCw size={14} className="mr-1.5" /> {t('cust_prof.recheck')}</>
                ) : (
                  <><CreditCard size={14} className="mr-1.5" /> {t('cust_prof.check_limit')}</>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Success result + Go to POS */}
        {(limitResult || (customer.murabaha_limit && Number(customer.murabaha_limit) > 0 && !isLimitExpired(customer))) && (
          <div className="space-y-3 animate-fade-in">
            {limitResult && (
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg flex items-start gap-3">
                <CheckCircle size={20} className="text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t('cust_prof.limit_success')}</p>
                  <p className="text-lg font-bold text-foreground mt-1">{formatUZS(limitResult.limit)}</p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Clock size={11} /> {t('cust_prof.validity_14')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('cust_prof.mock_mode')}</p>
                </div>
              </div>
            )}
            <Button
              className="w-full h-12 text-base"
              onClick={() => {
                clearCart();
                setMurabahaCustomer({
                  id: String(customer.id),
                  full_name: customer.full_name,
                  phone: customer.phone,
                  murabaha_limit: Number(customer.murabaha_limit ?? 0),
                  limit_expires_at: customer.limit_expires_at,
                });
                navigate('/pos');
              }}
            >
              <ShoppingCart size={18} className="mr-2" /> {t('cust_prof.go_pos')}
            </Button>
          </div>
        )}

        {/* Additional info for existing limit */}
        {customer.murabaha_limit && Number(customer.murabaha_limit) > 0 && !limitResult && (
          <div className="p-4 border border-dashed border-border rounded-lg">
            <p className="text-xs text-muted-foreground">
              {t('cust_prof.contract_soon')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
